import Spinner from '../../components/Spinner';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const COLORES = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2'];

function formatHora(h) {
  if (!h) return '—';
  const s = String(h).replace(/\.\d+/, '').padStart(4, '0');
  return `${s.slice(0,2)}:${s.slice(2)}`;
}

function getColor(retraso) {
  if (!retraso && retraso !== '0') return '#16a34a';
  const n = parseInt(retraso);
  if (n <= 0) return '#16a34a';
  if (n <= 30) return '#854f0b';
  return '#a32d2d';
}

function getLabelRetraso(retraso, haPasado) {
  if (!haPasado) return null;
  if (!retraso && retraso !== '0') return 'Puntual';
  const n = parseInt(retraso);
  if (n === 0) return 'Puntual';
  if (n < 0) return `${Math.abs(n)} min antes`;
  return `${n} min retraso`;
}

function MapaServicioInner({ trenes }) {
  const map = useMap();

  React.useEffect(() => {
    if (!trenes || trenes.length === 0) return;
    const layers = [];
    const BASE_DEPS = "https://services7.arcgis.com/XTupIrLX53AjaJqO/arcgis/rest/services/Dependencias_Adif_p%C3%BAblico/FeatureServer/0/query";

    const cargar = async () => {
      const bounds = [];
      const estacionesVistas = new Set();

      for (let ti = 0; ti < trenes.length; ti++) {
        const t = trenes[ti];
        const color = COLORES[ti % COLORES.length];
        if (!t.siges?.paradas) continue;

        const coords = [];
        for (const p of t.siges.paradas) {
          const cod = p.estacion.split('-')[0].trim();
          try {
            const r = await fetch(`${BASE_DEPS}?f=geojson&where=COD_DEPEND='${cod}'&outFields=COD_DEPEND,NOMBRE&resultRecordCount=1`);
            const data = await r.json();
            if (data.features?.length > 0) {
              const c = data.features[0].geometry?.coordinates;
              const nombre = data.features[0].properties?.NOMBRE || p.estacion.split('-').slice(1).join('-').trim();
              if (c) {
                coords.push([c[1], c[0]]);
                // Marcador de estación (sin duplicar)
                if (!estacionesVistas.has(cod)) {
                  estacionesVistas.add(cod);
                  const m = L.circleMarker([c[1], c[0]], {
                    radius: 6, fillColor: '#475569', color: 'white', weight: 2, fillOpacity: 1,
                    pane: 'markerPane'
                  }).addTo(map);
                  m.bindPopup(`<strong>${nombre}</strong>`);
                  layers.push(m);
                }
              }
            }
          } catch {}
        }

        if (coords.length > 1) {
          // Offset para que las líneas no se superpongan
          const offsetCoords = coords.map((pt, i) => {
            if (i === 0 || i === coords.length - 1) return pt;
            const prev = coords[i-1];
            const next = coords[i+1];
            const dx = next[1] - prev[1];
            const dy = next[0] - prev[0];
            const len = Math.sqrt(dx*dx + dy*dy) || 1;
            const offsetDeg = 0.004 * (ti - (trenes.length-1)/2);
            return [pt[0] + (-dx/len) * offsetDeg, pt[1] + (dy/len) * offsetDeg];
          });
          const linea = L.polyline(offsetCoords, { color, weight: 4, opacity: 0.9 }).addTo(map);
          linea.bindPopup(`<strong>Tren ${t.numero_tren}</strong>`);
          layers.push(linea);
          coords.forEach(c => bounds.push(c));

          // Marcadores inicio y fin
          [0, coords.length - 1].forEach((idx) => {
            const m = L.circleMarker(coords[idx], {
              radius: 7, fillColor: color, color: 'white', weight: 2, fillOpacity: 1
            }).addTo(map);
            m.bindPopup(`<strong>Tren ${t.numero_tren}</strong><br>${idx === 0 ? t.estacion_inicio : t.estacion_fin}`);
            layers.push(m);
          });
        }
      }

      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    };

    cargar();
    return () => layers.forEach(l => { try { map.removeLayer(l); } catch {} });
  }, [map, trenes]);

  return null;
}

export default function ServicioDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { cargar(); }, [id, fecha]);

  const cargar = () => {
    setLoading(true);
    api.get(`/siges/servicio-completo/${id}?fecha=${fecha}`)
      .then(r => setDatos(r.data))
      .finally(() => setLoading(false));
  };

  if (loading) return <Spinner />;
  if (!datos) return <div style={{ padding: 40, color: '#888' }}>No se encontraron datos</div>;

  const { servicio, trenes } = datos;

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(-1)}
          style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
          ← Volver
        </button>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{servicio.codigo}</h2>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{servicio.lote}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
        </div>
      </div>

      {/* Info locomotora */}
      {servicio.locomotora_numero && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e5e5', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🚂</span>
          <span style={{ fontWeight: 500 }}>{servicio.locomotora_numero}</span>
        </div>
      )}

      {/* Mapa */}
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e5e5', height: 350, marginBottom: 20 }}>
        <MapContainer center={[40.4, -3.7]} zoom={6} style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
          <MapaServicioInner trenes={trenes} />
        </MapContainer>
      </div>

      {/* Trenes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {trenes.map((t, ti) => {
          const color = COLORES[ti % COLORES.length];
          const paradas = t.siges?.paradas || [];
          const haPasado = paradas.some(p => p.tipo);
          const ultimaParada = [...paradas].reverse().find(p => p.tipo);
          const retrasoFinal = ultimaParada ? (ultimaParada.retraso_salida || ultimaParada.retraso_llegada) : null;

          return (
            <div key={t.id} style={{ background: '#fff', borderRadius: 12, border: `2px solid ${color}20`, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                onClick={() => setExpandido(expandido === t.id ? null : t.id)}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Tren {t.numero_tren}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {t.estacion_inicio} → {t.estacion_fin}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {haPasado ? (
                    <span style={{ fontSize: 12, fontWeight: 500, color: getColor(retrasoFinal) }}>
                      {getLabelRetraso(retrasoFinal, true)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#bbb' }}>Sin datos</span>
                  )}
                </div>
                <span style={{ color: '#aaa', fontSize: 12 }}>{expandido === t.id ? '▲' : '▼'}</span>
              </div>

              {expandido === t.id && (
                <div style={{ borderTop: '1px solid #f0f0f0' }}>
                  {paradas.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f8f8f6' }}>
                          <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 500, color: '#555' }}>Estación</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Hora real</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Situación</th>
                          <th style={{ padding: '8px 16px', textAlign: 'center', fontWeight: 500, color: '#555' }}>Parada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paradas.map((p, i) => {
                          const haPasadoP = !!p.tipo;
                          const horaReal = p.salida_real || p.llegada_real;
                          const horaTeo = p.salida_teo || p.llegada_teo;
                          const retraso = p.retraso_salida || p.retraso_llegada;
                          let paradaMin = null;
                          if (haPasadoP && p.llegada_real && p.salida_real && p.llegada_real !== p.salida_real) {
                            const toMin = h => { const s = String(h).replace(/\.\d+/,'').padStart(4,'0'); return parseInt(s.slice(0,2))*60+parseInt(s.slice(2)); };
                            const diff = toMin(p.salida_real) - toMin(p.llegada_real);
                            if (diff > 0) paradaMin = diff;
                          }
                          return (
                            <tr key={i} style={{ borderTop: '1px solid #f0f0f0', opacity: haPasadoP ? 1 : 0.4 }}>
                              <td style={{ padding: '8px 16px', fontWeight: 500 }}>{p.estacion}</td>
                              <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                {haPasadoP
                                  ? <span style={{ fontWeight: 500 }}>{formatHora(horaReal)}</span>
                                  : <span style={{ color: '#bbb', fontStyle: 'italic' }}>{formatHora(horaTeo)} (teo.)</span>
                                }
                              </td>
                              <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                {haPasadoP && (
                                  <span style={{ color: getColor(retraso), fontWeight: 500 }}>
                                    {getLabelRetraso(retraso, true)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                                {paradaMin && (
                                  <span style={{ background: '#faeeda', color: '#854f0b', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>
                                    {paradaMin} min
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 16, color: '#aaa', fontSize: 13, textAlign: 'center' }}>
                      Sin datos SIGES para esta fecha
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
